/**
 * The only place this app calls fetch.
 *
 * Everything is same-origin: in production the Worker serves this build, in dev
 * Vite proxies the API paths through. So the session cookie travels on its own
 * and there is no token to store anywhere a script can read it.
 *
 * Hand-written rather than generated from the OpenAPI document. The document is
 * there and generating would be tidier, but it adds a build step between a
 * backend change and a frontend that notices, and this console calls eight
 * endpoints. Revisit if that number doubles.
 */

import type {
    Account,
    ApiKey,
    IssuedApiKey,
    ConsoleSearchRequest,
    ConsoleSearchResult,
    DemoSearchResult,
} from '../types';

const BASE = '/api';

export class ApiError extends Error {
    constructor(
        message: string,
        public status: number,
        public code: string,
    ) {
        super(message);
        this.name = 'ApiError';
    }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
    const res = await fetch(`${BASE}${path}`, {
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' }, ...init,
    });

    if (!res.ok) {
        // The API answers every failure in one envelope, so the message is
        // safe to show the user as-is, it was written for them.
        const body = (await res.json().catch(() => null)) as { error?: string; message?: string } | null;
        throw new ApiError(body?.message ?? `Request failed (${res.status})`, res.status, body?.error ?? 'UNKNOWN');
    }

    return (await res.json()) as T;
}

export const api = {
    requestLink: (email: string) =>
        request<{ sent: boolean; devLink?: string }>('/auth/request', {
            method: 'POST',
            body: JSON.stringify({ email }),
        }),

    verifyLink: (token: string) =>
        request<{ user: { id: string; email: string }; expiresAt: string }>('/auth/verify', {
            method: 'POST',
            body: JSON.stringify({ token }),
        }),

    me: () => request<Account>('/auth/me'),

    logout: () => request<{ ok: boolean }>('/auth/logout', { method: 'POST' }),

    listKeys: () => request<{ keys: ApiKey[] }>('/keys'),

    createKey: (name: string) =>
        request<IssuedApiKey>('/keys', { method: 'POST', body: JSON.stringify({ name }) }),

    revokeKey: (id: string) => request<{ revoked: true }>(`/keys/${id}`, { method: 'DELETE' }),

    /**
     * Dashboard try, session cookie only, same quota as /v1.
     * Integrators use Bearer keys on /v1; never this path.
     */
    consoleSearch: (body: ConsoleSearchRequest) =>
        request<ConsoleSearchResult>('/console/search', {
            method: 'POST',
            body: JSON.stringify(body),
        }),

    /**
     * Public marketing demo, fixed keywords, D1 cache only. No credentials.
     * Live free-text search stays on consoleSearch / /v1.
     */
    demoSearch: (keyword: string) =>
        request<DemoSearchResult>(`/demo/search?keyword=${encodeURIComponent(keyword)}`),

    /** A single-use Stripe portal URL. Cancelling and card updates both live there. */
    billingPortal: () => request<{ url: string }>('/billing/portal', { method: 'POST' }),

    /**
     * Hosted Checkout for a paid tier. Public, no session required. Server maps
     * `plan` to a bound Stripe price id; the raw id never leaves the Worker.
     */
    billingCheckout: (plan: 'starter' | 'growth' | 'scale') =>
        request<{ url: string }>('/billing/checkout', {
            method: 'POST',
            body: JSON.stringify({ plan }),
        }),

    /**
     * Launch list. Stand-in for checkout only while `BILLING_LIVE` is false; `source`
     * carries which tier was clicked, which is the only demand signal available
     * until cards are open. Already-listed is a 200, not an error.
     */
    joinWaitlist: (email: string, source?: string) =>
        request<{ joined: boolean }>('/waitlist', {
            method: 'POST',
            body: JSON.stringify(source ? { email, source } : { email }),
        }),
};
